/**
 * Настройки: организация, профиль, вход, сессии.
 *
 * Экран видят все четыре роли, но состоит он у них из разного: блок организации
 * приходит только тому, кто дотягивается до `PATCH /organizations/current`.
 * Клиент агентства открывает эту же страницу и видит ровно профиль, способы
 * входа и свои сессии — ни названия Seeno, ни чужих данных здесь нет.
 *
 * Три поведения, которые нельзя было сделать иначе.
 *
 * * **Почта не редактируется полем.** Смена адреса — отдельный поток: письмо на
 *   новый адрес, уведомление на старый, запись в историю. Для B2B-аккаунта это
 *   материал для разбора инцидента, а не «сохранить».
 * * **Смена пароля отзывает все сессии, кроме текущей.** Это не побочный
 *   эффект, а смысл действия: пароль меняют, когда думают, что его знает
 *   кто-то ещё. Экран говорит об этом до нажатия.
 * * **«Выйти на всех устройствах» отзывает и текущую сессию тоже.** Иначе
 *   действие называлось бы иначе. После него человек оказывается на форме
 *   входа — и это правильный конец, а не сбой.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { KvRow, Kv, Field, Panel } from '../ui/form';
import { Dialog } from '../ui/Dialog';
import { ErrorNotice, Skeleton } from '../ui/state';
import { StepUpCancelled, useConfirmed } from '../ui/StepUp';
import { useAccess, useSessionState } from '../session';
import { ApiError, fieldErrors } from '../api/problem';
import {
  changePassword,
  getAccount,
  listLoginMethods,
  listSessions,
  logoutAll,
  requestEmailChange,
  revokeSession,
  updateAccount,
  updateOrganization,
  getOrganization,
  type Account,
  type LoginMethods,
  type Organization,
  type UserSession,
} from '../api/account';
import { dateProse, momentProse } from '@shared/src/format';
import { PASSWORD_HINT } from '../ui/password';
import { PAYER_SNAPSHOT } from '@shared/src/product';

interface Bundle {
  account: Account;
  methods: LoginMethods;
  sessions: UserSession[];
  organization: Organization | null;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; data: Bundle }
  | { kind: 'failed'; error: unknown };

/**
 * Адрес устройства для графы «Адрес».
 *
 * Приёмка увидела здесь `176.12.77.123/32` — не адрес, а запись подсети. Маску
 * дописывал сервер: колонка объявлена как `inet`, и приведение к тексту в
 * PostgreSQL печатает длину маски всегда, даже когда её никто не задавал.
 * Починено там же, в `seeno-ru-api` (`host(ip)`), но обрезка остаётся и здесь:
 * в списке собственных телефонов и ноутбуков `/32` не значит ничего ни для
 * кого, а показать его снова может любой источник, который завтра пойдёт мимо
 * того запроса. Стоит одну строку.
 */
function address(value: string | null | undefined): string {
  if (!value) return '—';
  const [host] = value.split('/', 1);
  return host ?? '—';
}

export function Settings() {
  const access = useAccess();
  const confirmed = useConfirmed();
  const { signOut } = useSessionState();

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [orgName, setOrgName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [leaving, setLeaving] = useState(false);

  const manages = access.reaches('PATCH', '/organizations/current');

  useEffect(() => {
    const abort = new AbortController();
    Promise.all([
      getAccount(abort.signal),
      listLoginMethods(abort.signal),
      listSessions(abort.signal),
      manages ? getOrganization(abort.signal) : Promise.resolve(null),
    ])
      .then(([account, methods, sessions, organization]) => {
        setName(account.full_name);
        setPhone(account.phone ?? '');
        if (organization) {
          setOrgName(organization.name);
          setBillingEmail(organization.billing_email);
        }
        setLoad({ kind: 'ready', data: { account, methods, sessions, organization } });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });
    return () => abort.abort();
  }, [attempt, manages]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  const act = useCallback(
    async (key: string, run: () => Promise<string>) => {
      setBusy(key);
      setFailure(null);
      setNote(null);
      setFields({});
      try {
        setNote(await run());
        reload();
      } catch (error) {
        if (error instanceof StepUpCancelled) return;
        if (error instanceof ApiError) setFields(fieldErrors(error.problem));
        setFailure(error);
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  const head = (
    <div className="screen-head">
      <h1>Настройки</h1>
    </div>
  );

  if (load.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }
  if (load.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice
          error={load.error}
          action={
            <button className="btn btn-ghost btn-sm" type="button" onClick={reload}>
              Повторить
            </button>
          }
        />
      </>
    );
  }

  const { account, methods, sessions, organization } = load.data;

  return (
    <>
      {head}

      {failure ? <ErrorNotice error={failure} /> : null}
      {note ? (
        <p className="notice ok" role="status">
          <b>Готово</b>
          {note}
        </p>
      ) : null}

      {/* --- организация --- */}
      {organization ? (
        <Panel
          title="Организация"
          lead="Название видно участникам и стоит в отчётах. Почта для счетов — адрес, куда уходят счета, акты и уведомления о списаниях."
        >
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void act('organization', async () => {
                await updateOrganization({ name: orgName, billing_email: billingEmail });
                return 'Данные организации сохранены.';
              });
            }}
            noValidate
          >
            <Field
              id="org-name"
              label="Название организации"
              hint="Так вас видят участники и клиенты в отчётах"
              error={fields.name}
            >
              {(props) => (
                <input
                  {...props}
                  type="text"
                  required
                  value={orgName}
                  onChange={(event) => setOrgName(event.target.value)}
                />
              )}
            </Field>

            <Field
              id="org-email"
              label="Почта для счетов"
              hint="Сюда уходят счета, акты и уведомление за сутки до каждого списания"
              error={fields.billing_email}
            >
              {(props) => (
                <input
                  {...props}
                  type="email"
                  required
                  value={billingEmail}
                  onChange={(event) => setBillingEmail(event.target.value)}
                />
              )}
            </Field>

            <button
              className="btn btn-primary btn-sm"
              type="submit"
              disabled={busy === 'organization'}
            >
              Сохранить
            </button>
          </form>

          <Kv>
            <KvRow label="Юрлицо">
              {(organization.legal_details?.legal_name as string) ?? 'Не указано'}
            </KvRow>
            <KvRow label="ИНН">
              {(organization.legal_details?.inn as string) ?? 'Не указан'}
            </KvRow>
            <KvRow label="Реквизиты в документах" note={PAYER_SNAPSHOT}>
              Копируются на момент выписки
            </KvRow>
            {organization.created_at ? (
              <KvRow label="Организация заведена">
                {dateProse(organization.created_at)}
              </KvRow>
            ) : null}
          </Kv>
        </Panel>
      ) : null}

      {/* --- профиль --- */}
      <Panel
        title="Профиль"
        lead="Имя видят участники организации: в приглашениях, в отчётах и рядом с вашими действиями."
      >
        <form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void act('account', async () => {
              await updateAccount({ full_name: name, phone: phone || null });
              return 'Профиль сохранён.';
            });
          }}
          noValidate
        >
          <Field
            id="account-name"
            label="Имя и фамилия"
            hint="Показывается участникам организации рядом с вашими действиями"
            error={fields.full_name}
          >
            {(props) => (
              <input
                {...props}
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <Field
            id="account-phone"
            label="Телефон"
            hint="Второй шаг при входе и канал критичных оповещений. Входить по номеру нельзя — только по почте"
            error={fields.phone}
          >
            {(props) => (
              <input
                {...props}
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            )}
          </Field>

          <button
            className="btn btn-primary btn-sm"
            type="submit"
            disabled={busy === 'account'}
          >
            Сохранить
          </button>
        </form>
      </Panel>

      {/* --- почта --- */}
      <Panel
        title="Адрес почты"
        lead="Почта — это ваш вход в продукт, поэтому смена подтверждается письмом."
      >
        <Kv>
          <KvRow
            label="Текущий адрес"
            note={account.email_verified ? 'подтверждён' : 'не подтверждён'}
          >
            {account.email}
          </KvRow>
        </Kv>

        <form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void act('email', async () => {
              await confirmed('account.email_change', (token) =>
                requestEmailChange(newEmail.trim(), token),
              );
              const target = newEmail.trim();
              setNewEmail('');
              return `Письмо со ссылкой ушло на ${target}. Адрес сменится, когда вы перейдёте по ней. На старый адрес ушло уведомление о попытке.`;
            });
          }}
          noValidate
        >
          <Field
            id="account-email"
            label="Новый адрес"
            hint="Ссылка уйдёт на новый адрес, уведомление о смене — на старый"
            error={fields.email}
          >
            {(props) => (
              <input
                {...props}
                type="email"
                autoComplete="email"
                required
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
              />
            )}
          </Field>

          <button
            className="btn btn-ghost btn-sm"
            type="submit"
            disabled={busy === 'email'}
          >
            Отправить подтверждение
          </button>
        </form>
      </Panel>

      {/* --- пароль и способы входа --- */}
      <Panel
        title="Пароль и способы входа"
        lead="Пароль и Яндекс ID работают независимо: любым можно войти. Последний способ снять нельзя — без него в аккаунт не попасть."
      >
        <Kv>
          <KvRow label="Пароль">{methods.has_password ? 'Задан' : 'Не задан'}</KvRow>
          {methods.identities.map((identity) => (
            <KvRow
              key={identity.provider}
              label="Яндекс ID"
              note={
                identity.last_login_at
                  ? `последний вход ${dateProse(identity.last_login_at)}`
                  : 'входа ещё не было'
              }
            >
              {identity.provider_email ?? 'привязан'}
            </KvRow>
          ))}
        </Kv>

        <form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void act('password', async () => {
              await changePassword(currentPassword, nextPassword);
              setCurrentPassword('');
              setNextPassword('');
              return 'Пароль изменён. Все остальные сессии отозваны — на других устройствах придётся войти заново.';
            });
          }}
          noValidate
        >
          <Field
            id="password-current"
            label="Текущий пароль"
            hint="Нужен, чтобы сменить пароль без письма"
            error={fields.current_password}
          >
            {(props) => (
              <input
                {...props}
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            )}
          </Field>

          <Field
            id="password-next"
            label="Новый пароль"
            hint={PASSWORD_HINT}
            error={fields.new_password}
          >
            {(props) => (
              <input
                {...props}
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={nextPassword}
                onChange={(event) => setNextPassword(event.target.value)}
              />
            )}
          </Field>

          <button
            className="btn btn-primary btn-sm"
            type="submit"
            disabled={busy === 'password'}
          >
            Сменить пароль
          </button>
        </form>
      </Panel>

      {/* --- сессии --- */}
      <Panel
        title="Устройства и сессии"
        lead="Каждая строка — браузер, в котором вы вошли. Отзыв закрывает доступ немедленно."
        action={
          <button
            className="btn btn-danger btn-sm"
            type="button"
            onClick={() => setLeaving(true)}
          >
            Выйти на всех устройствах
          </button>
        }
      >
        <div className="tbl-wrap">
          <table className="mini">
            <caption className="stamp">
              Активные сессии учётной записи · всего {sessions.length}
            </caption>
            <thead>
              <tr>
                <th scope="col">Устройство</th>
                <th scope="col">Последняя работа</th>
                <th scope="col">Адрес</th>
                <th scope="col">
                  <span className="visually-hidden">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((item) => (
                <tr key={item.id}>
                  <td>
                    <b>{item.device_label ?? 'Неизвестное устройство'}</b>
                    <span className="stamp">
                      вход {dateProse(item.created_at)}
                      {item.current ? ' · это устройство' : ''}
                    </span>
                  </td>
                  <td className="tnum">{momentProse(item.last_used_at)}</td>
                  <td className="tnum">{address(item.ip)}</td>
                  <td className="right">
                    {item.current ? (
                      <span className="stamp">текущая</span>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        disabled={busy === `session-${item.id}`}
                        onClick={() =>
                          act(`session-${item.id}`, async () => {
                            await revokeSession(item.id);
                            return 'Сессия отозвана. На том устройстве потребуется войти заново.';
                          })
                        }
                      >
                        Отозвать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog
        open={leaving}
        title="Выйти на всех устройствах"
        onClose={() => setLeaving(false)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setLeaving(false)}
            >
              Остаться
            </button>
            <button
              className="btn btn-danger btn-sm"
              type="button"
              onClick={() => {
                setLeaving(false);
                void logoutAll()
                  .catch(() => undefined)
                  .finally(signOut);
              }}
            >
              Выйти везде
            </button>
          </>
        }
      >
        <p className="dlg-lead">
          Отзовём все сессии, включая эту: сейчас вы окажетесь на форме входа. Так делают,
          когда потеряли ноутбук или подозревают, что пароль знает кто-то ещё.
        </p>
      </Dialog>
    </>
  );
}
